/**
 * Hand-written mirror of the waba-api Announcement/IconAsset DTOs (see
 * waba/src/main/java/.../domain/model/{Announcement,AnnouncementFeature,IconAsset,...}.java).
 *
 * These are NOT generated by ng-openapi-gen — the waba-api-swagger.yaml checked into this repo
 * is only regenerated from a live server (npm run gn-waba), and the new endpoints haven't been
 * deployed yet. Shapes here are kept field-for-field identical to the Java DTOs (see
 * ApiResponse<T>/PagedResult<T> shape already used by the real generated models, e.g.
 * services/generated/waba-api/models/video-tutorial.ts, for the envelope convention). Once waba
 * is redeployed with these controllers and `npm run gn-waba` is re-run, this file and its sibling
 * *-api.service.ts files can be deleted in favor of the real generated ones.
 */

export type AnnouncementDisplayStyle = "MODAL_TWO_PANE" | "MODAL_SIMPLE" | "TOP_BANNER";
export type AnnouncementTagTone = "NEUTRAL" | "NEW" | "BETA" | "PAID";
export type AnnouncementCtaType = "NONE" | "LINK" | "INTERNAL_ROUTE";
export type AnnouncementIconType = "LIBRARY" | "UPLOAD";
export type AnnouncementPreviewMediaType = "NONE" | "IMAGE" | "GIF" | "VIDEO";
export type AnnouncementSeenStatus = "SEEN" | "DISMISSED" | "CLICKED";
export type AnnouncementIconTone = "NONE" | "LIGHT" | "DARK";
export type AnnouncementEffectType = "CONFETTI" | "SNOW" | "SPARKLES" | "FIREWORKS" | "BUBBLES";
export type AnnouncementEffectIntensity = "SUBTLE" | "NORMAL" | "FESTIVE";
/** Where the particles play: over the whole screen around the popup, or inside the card. */
export type AnnouncementEffectOrigin = "SCREEN" | "CARD";
/**
 * Which kind of company sees it: regular clients, or companies still on the free trial. The two
 * never mix — waba serves a trial company only the TRIAL ones until it is converted to client.
 */
export type AnnouncementClientSegment = "CLIENT" | "TRIAL";

export interface AnnouncementFeature {
  id?: string;
  order?: number;
  iconType?: AnnouncementIconType;
  iconValue?: string;
  /** Icon chip background; null uses the panel default. */
  iconBg?: string | null;
  /** Forces the icon to a flat white/black silhouette so it never blends into iconBg. */
  iconTone?: AnnouncementIconTone;
  title?: Record<string, string>;
  description?: Record<string, string>;
  tagLabel?: Record<string, string> | null;
  tagTone?: AnnouncementTagTone | null;
  ctaType?: AnnouncementCtaType;
  ctaLabel?: Record<string, string> | null;
  ctaTarget?: string | null;
  previewMediaType?: AnnouncementPreviewMediaType;
  previewMediaUrl?: string | null;
}

/** Optional bar pinned to the bottom of the panel (icon + bold lead + note + trailing link). */
export interface AnnouncementFooter {
  enabled?: boolean;
  iconValue?: string | null;
  /** Bar background; null keeps the translucent tint of the panel colour. */
  bg?: string | null;
  /** Bar text colour; null follows the panel's text colour. */
  textColor?: string | null;
  /** Icon chip background; null uses the default tint. */
  iconBg?: string | null;
  /** Forces the icon to a flat white/black silhouette so it never blends into iconBg. */
  iconTone?: AnnouncementIconTone;
  text?: Record<string, string>;
  note?: Record<string, string>;
  linkLabel?: Record<string, string>;
  linkUrl?: string | null;
}

/**
 * Optional decorative particle overlay drawn over the popup (confetti, snow…). Null or
 * disabled means no effect at all — announcements authored before this existed render
 * unchanged. Purely presentational, so it travels inside contentJson like theme/footer.
 */
export interface AnnouncementEffects {
  enabled?: boolean;
  type?: AnnouncementEffectType;
  intensity?: AnnouncementEffectIntensity;
  /** Null defaults to SCREEN — the whole page behind/around the popup, not just the card. */
  origin?: AnnouncementEffectOrigin | null;
  /** Custom particle colours; null/empty derives a palette from the theme accent. */
  colors?: string[] | null;
}

/** Per-announcement colour overrides; null fields fall back to the component defaults. */
export interface AnnouncementTheme {
  panelBg?: string | null;
  panelText?: string | null;
  previewBg?: string | null;
  accent?: string | null;
  accentText?: string | null;
}

export interface Announcement {
  id?: number;
  reference?: string;
  createdAt?: string;
  updatedAt?: string;
  slug?: string;
  isActive?: boolean;
  /**
   * Legacy wire spelling. waba used to publish the kill switch as "active" (Jackson derived the
   * name from Lombok's isActive() getter), which left `isActive` undefined here — every Active
   * switch in the list and the form rendered off. waba now sends "isActive"; this stays so the
   * UI reads correctly against an API that has not been redeployed yet. Never write to it.
   */
  active?: boolean;
  displayStyle?: AnnouncementDisplayStyle;
  showNotificationBadge?: boolean;
  persistInHeader?: boolean;
  publishAt?: string | null;
  expiresAt?: string | null;
  priority?: number;
  audienceRoles?: string[];
  audienceGroups?: string[];
  /** Missing on an API not redeployed yet — read it as "CLIENT", the server-side default. */
  clientSegment?: AnnouncementClientSegment;
  title?: Record<string, string>;
  subtitle?: Record<string, string>;
  features?: AnnouncementFeature[];
  footer?: AnnouncementFooter | null;
  theme?: AnnouncementTheme | null;
  effects?: AnnouncementEffects | null;
}

/**
 * Reads the kill switch from either wire spelling — see Announcement.active. Missing on both
 * means an announcement the API never told us about, which is active by default (that is the
 * server-side default too), not archived.
 */
export function isAnnouncementActive(announcement: Announcement | null | undefined): boolean {
  return announcement?.isActive ?? announcement?.active ?? true;
}

/**
 * Value of a `lang -> text` map for the language being rendered, falling back to English and
 * then to any language that has text.
 *
 * The admin form posts every language on every field and fills the ones nobody typed in with
 * `""` — never undefined. A `??` chain therefore stops at the first *empty string* instead of
 * falling through, which is what made a banner authored in English render as a blank strip for
 * a user in French, and a footer show its icon with no text next to it. Blank counts as
 * missing here.
 */
export function announcementText(
  map: Record<string, string> | undefined | null,
  lang: string,
): string {
  if (!map) return "";
  for (const key of [lang, "en", ...Object.keys(map)]) {
    const value = map[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return "";
}

export interface AnnouncementSearchReq {
  slug?: string;
  isActive?: boolean;
  displayStyle?: AnnouncementDisplayStyle;
}

export interface AnnouncementSeenRequest {
  featureId?: string | null;
  status: AnnouncementSeenStatus;
}

export interface AnnouncementInboxItem {
  announcement: Announcement;
  seen: boolean;
}

export interface AnnouncementStats {
  announcementReference?: string;
  seenCount?: number;
  dismissedCount?: number;
  clickedCount?: number;
}

export interface IconAsset {
  id?: number;
  reference?: string;
  createdAt?: string;
  updatedAt?: string;
  key?: string;
  label?: string;
  url?: string;
  category?: string;
  isActive?: boolean;
}

export interface IconAssetSearchReq {
  key?: string;
  category?: string;
  isActive?: boolean;
}

/** Mirrors commun.payload.ApiResponse<T>. */
export interface ApiResponse<T> {
  body?: T;
  i18nErrorCode?: string;
  message?: string;
  requestID?: string;
  status?: string;
  timeStamp?: string;
}

/** Mirrors commun.payload.pagination.PagedResult<T>. */
export interface PagedResult<T> {
  entities?: T[];
  last?: boolean;
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
}

/** Mirrors commun.payload.pagination.PagedRequest<T>. */
export interface PagedRequest<T> {
  page: number;
  size: number;
  sort?: "ASC" | "DESC";
  sortField?: string;
  filter?: T;
}
